/**
 * ============================================================================
 * AUTHENTICATION HOOK - useAuth
 * ============================================================================
 * Centralized authentication state and logic
 * Version: v3.10.0-CLEAR-MAP
 * 
 * This hook manages:
 * - User authentication state
 * - Profile management
 * - OAuth flows (Google, Microsoft)
 * - Attribution system
 * - Session management
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { ATTRIBUTION_CONFIG, OAUTH_CONFIG } from '../utils/constants';
import * as api from '../services/apiService';

// Create Auth Context
const AuthContext = createContext(null);

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  // ============================================================================
  // STATE
  // ============================================================================
  
  // Core auth state
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState('');
  
  // Attribution & Profile state
  const [submittedOnBehalfOf, setSubmittedOnBehalfOf] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [showSubmittedByModal, setShowSubmittedByModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('signup'); // 'signup' or 'signin'
  const [showAttributionModal, setShowAttributionModal] = useState(false);
  
  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================
  
  /**
   * Load user profile from localStorage
   */
  const loadUserProfile = () => {
    try {
      const savedProfile = localStorage.getItem('user_profile');
      if (savedProfile) {
        const profile = JSON.parse(savedProfile);
        setUserProfile(profile);
        // If we have a real email, prefer it as the canonical identity for routing (notifications, team reports, etc.)
        // Otherwise fall back to a display name.
        const profileEmail = (profile.email || '').trim().toLowerCase();
        setSubmittedOnBehalfOf(profileEmail && profileEmail.includes('@') ? profileEmail : (profile.name || profile.displayName || ''));
        return profile;
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
    return null;
  };

  /**
   * Save user profile to localStorage
   */
  const saveUserProfile = (profile) => {
    try {
      localStorage.setItem('user_profile', JSON.stringify(profile));
      setUserProfile(profile);
      // Prefer real email as the "submitted_on_behalf_of" identity (used for routing + notifications).
      const profileEmail = (profile.email || '').trim().toLowerCase();
      setSubmittedOnBehalfOf(profileEmail && profileEmail.includes('@') ? profileEmail : (profile.name || profile.displayName || ''));
      
      // Update currentUser state for header display
      const userName = profile.name || profile.displayName || 'User';
      setCurrentUser(userName);
      localStorage.setItem('currentUser', userName);
      
    } catch (error) {
      console.error('Error saving user profile:', error);
    }
  };

  /**
   * Extract first name from email
   */
  const getFirstNameFromEmail = (email) => {
    if (!email || typeof email !== 'string' || !email.trim()) return 'Not logged in';
    
    // Extract first part before @
    const namePart = email.split('@')[0];
    
    // Convert dots to spaces and capitalize first letter of each word
    const nameParts = namePart.split('.');
    const firstName = nameParts[0];
    
    // Capitalize first letter
    return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
  };

  /**
   * Get submitted_by name from inspection
   */
  const getSubmittedByName = (inspection) => {
    // Try to get from submitted_by field first
    let submittedBy = inspection.submitted_by;
    
    // If missing or Anonymous, try to extract from notes/additional_notes
    if (!submittedBy || submittedBy === 'Anonymous') {
      const notesToCheck = inspection.additional_notes || inspection.notes || '';
      const match = notesToCheck.match(/Submitted by:\s*([^\n\r]+)/i);
      if (match && match[1].trim()) {
        submittedBy = match[1].trim();
      }
    }
    
    return submittedBy || 'Anonymous';
  };

  /**
   * Validate Pye-Barker email format
   */
  const validatePyeBarkerEmail = (email) => {
    const pyeBarkerDomains = ['pyebarker.com', 'pyebarkerfs.com'];
    const emailLower = email.toLowerCase();
    return pyeBarkerDomains.some(domain => emailLower.endsWith(`@${domain}`));
  };

  /**
   * Set submitted on behalf of user (used by onboarding)
   */
  const setSubmittedOnBehalfOfUser = (email) => {
    if (!email || !email.trim()) {
      console.error('❌ Email address is required');
      return false;
    }
    
    const trimmedEmail = email.trim().toLowerCase();
    
    // Extract name from email for display
    const displayName = trimmedEmail.split('@')[0].replace('.', ' ').replace('_', ' ');
    const capitalizedName = displayName.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
    
    // Set all auth states
    setSubmittedOnBehalfOf(trimmedEmail);
    setCurrentUser(capitalizedName);
    
    // Persist to localStorage
    localStorage.setItem('submitted_on_behalf_of', trimmedEmail);
    localStorage.setItem('currentUser', capitalizedName);
    
    
    return true;
  };

  /**
   * Clear submitted on behalf
   */
  const clearSubmittedOnBehalf = () => {
    setSubmittedOnBehalfOf('');
    localStorage.removeItem('submitted_on_behalf_of');
  };

  /**
   * Logout user - Clear all data and return to onboarding
   */
  const logoutUser = () => {
    // Clear user data
    localStorage.removeItem('user_profile');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('session_token');
    localStorage.removeItem('submittedOnBehalfOf');
    localStorage.removeItem('token');
    localStorage.removeItem('cachedMyScore');
    localStorage.removeItem('submitted_on_behalf_of');
    
    // Clear onboarding flag to show onboarding again
    localStorage.removeItem('pwaOnboardingComplete');
    
    // Clear permission flags (will ask again on next login)
    localStorage.removeItem('cameraPermissionGranted');
    localStorage.removeItem('locationPermissionGranted');
    
    // Reset state
    setUserProfile(null);
    setCurrentUser('');
    setUser(null);
    
    
    // Force reload to show onboarding
    window.location.href = '/';
  };

  /**
   * Check if attribution is required
   */
  const checkAttributionRequired = () => {
    if (!ATTRIBUTION_CONFIG.enabled) return false;
    if (!ATTRIBUTION_CONFIG.required) return false;
    return !userProfile && !submittedOnBehalfOf.trim();
  };

  /**
   * Handle quick attribution (name only)
   */
  const handleQuickAttribution = (name, email = '', showToast) => {
    const profile = {
      name: name.trim(),
      email: email.trim(),
      type: 'quick',
      createdAt: new Date().toISOString()
    };
    saveUserProfile(profile);
    setCurrentUser(name.trim());
    // If they provided an email, use it as canonical identity for routing + notifications.
    const emailLower = (email || '').trim().toLowerCase();
    if (emailLower && emailLower.includes('@')) {
      setSubmittedOnBehalfOf(emailLower);
      localStorage.setItem('submitted_on_behalf_of', emailLower);
    } else {
      setSubmittedOnBehalfOf(name.trim());
    }
    localStorage.setItem('currentUser', name.trim());
    setShowAttributionModal(false);
    
    // Auto-assign RSLs/ASMs to themselves
    if (email && email.includes('@pyebarkerfs.com')) {
      autoAssignToSelfIfRSL(email.trim());
    }
    
    if (showToast) {
      showToast('👤 Profile saved! Auto-fill enabled for future submissions.', 'success', 3000);
    }
  };
  
  /**
   * Auto-assign regional sales leaders/managers to themselves
   */
  const autoAssignToSelfIfRSL = async (email) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Check if this email is a regional officer
      const officersRes = await fetch(`${backendUrl}/api/admin/regional-officers`, {
        headers: { 'X-Local-Dev': 'true' }
      });
      const officersData = await officersRes.json();
      
      if (officersData.success && officersData.officers) {
        const matchingOfficer = officersData.officers.find(
          o => o.email.toLowerCase() === email.toLowerCase()
        );
        
        if (matchingOfficer) {
          
          // Determine assignment based on role
          if (matchingOfficer.role === 'regional_sales_leader' || !matchingOfficer.role) {
            // Assign RSL to themselves
            // Base64 encode email to bypass WAF
            const encodedEmail = btoa(email);
            await fetch(`${backendUrl}/api/admin/users/${encodedEmail}/sales-leader`, {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                'X-Local-Dev': 'true'
              },
              body: JSON.stringify({
                sales_leader_id: matchingOfficer.id,
                sales_leader_name: matchingOfficer.name,
                sales_leader_email: matchingOfficer.email,
                area_sales_manager_id: null,
                area_sales_manager_name: '',
                area_sales_manager_email: ''
              })
            });
          } else if (matchingOfficer.role === 'area_sales_manager' && matchingOfficer.reports_to) {
            // Assign ASM to their RSL and themselves
            const rsl = officersData.officers.find(o => o.id === matchingOfficer.reports_to);
            if (rsl) {
              // Base64 encode email to bypass WAF
              const encodedEmail = btoa(email);
              await fetch(`${backendUrl}/api/admin/users/${encodedEmail}/sales-leader`, {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                  'X-Local-Dev': 'true'
                },
                body: JSON.stringify({
                  sales_leader_id: rsl.id,
                  sales_leader_name: rsl.name,
                  sales_leader_email: rsl.email,
                  area_sales_manager_id: matchingOfficer.id,
                  area_sales_manager_name: matchingOfficer.name,
                  area_sales_manager_email: matchingOfficer.email
                })
              });
            }
          }
        }
      }
    } catch (error) {
      // Auto-assignment failed - fail silently
      // Fail silently - don't disrupt user experience
    }
  };

  /**
   * Skip attribution (guest mode)
   */
  const skipAttribution = (showToast) => {
    if (!ATTRIBUTION_CONFIG.allowSkip && ATTRIBUTION_CONFIG.required) {
      if (showToast) {
        showToast('⚠️ Profile information is required for submissions.', 'error', 3000);
      }
      return;
    }
    setShowAttributionModal(false);
    setSubmittedOnBehalfOf('Guest User');
    setCurrentUser('Guest User');
    // Clear any previous user data and set Guest User as temporary session
    localStorage.removeItem('currentUser');
    sessionStorage.setItem('currentUser', 'Guest User');
    if (showToast) {
      showToast('👤 Continuing as guest. You can set up your profile later.', 'info', 3000);
    }
  };

  /**
   * Handle Google OAuth - FAIL CLOSED (no demo fallback)
   */
  const handleGoogleOAuth = (mode = 'signup', showToast) => {
    
    // SECURITY: Fail closed if OAuth is not properly configured
    if (!OAUTH_CONFIG.google.clientId || 
        OAUTH_CONFIG.google.clientId === 'demo-google-client-id' ||
        OAUTH_CONFIG.google.clientId.includes('demo')) {
      console.error('❌ Google OAuth not configured - authentication blocked');
      if (showToast) {
        showToast('❌ Google authentication is not available. Please use email sign-up.', 'error');
      }
      return; // Fail closed - no fallback
    }
    
    // Real Google OAuth flow
    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${OAUTH_CONFIG.google.clientId}&` +
      `redirect_uri=${encodeURIComponent(OAUTH_CONFIG.google.redirectUri)}&` +
      `response_type=code&` +
      `scope=${encodeURIComponent(OAUTH_CONFIG.google.scopes)}&` +
      `state=${mode}`;
    
    window.location.href = googleAuthUrl;
  };

  /**
   * Handle Microsoft OAuth - FAIL CLOSED (no demo fallback)
   */
  const handleMicrosoftOAuth = (mode = 'signup', showToast) => {
    // SECURITY: Fail closed if OAuth is not properly configured
    if (!OAUTH_CONFIG.microsoft.clientId || 
        OAUTH_CONFIG.microsoft.clientId === 'demo-microsoft-client-id' ||
        OAUTH_CONFIG.microsoft.clientId.includes('demo')) {
      console.error('❌ Microsoft OAuth not configured - authentication blocked');
      if (showToast) {
        showToast('❌ Microsoft authentication is not available. Please use email sign-up.', 'error');
      }
      return; // Fail closed - no fallback
    }
    
    // Real Microsoft OAuth flow
    const microsoftAuthUrl = `https://login.microsoftonline.com/${OAUTH_CONFIG.microsoft.tenantId}/oauth2/v2.0/authorize?` +
      `client_id=${OAUTH_CONFIG.microsoft.clientId}&` +
      `redirect_uri=${encodeURIComponent(OAUTH_CONFIG.microsoft.redirectUri)}&` +
      `response_type=code&` +
      `scope=openid profile email&` +
      `state=${mode}`;
    
    window.location.href = microsoftAuthUrl;
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================
  
  /**
   * Check session on mount
   * 
   * SECURITY ARCHITECTURE:
   * - App loads for everyone (no blocking)
   * - New users get prompted for email during onboarding
   * - Protected features (admin, leaderboard) require MSAL token
   * - Simple email authentication for technicians via @pyebarkerfs.com
   */
  useEffect(() => {
    const checkSession = async () => {
      try {
        // Check for existing user profile (from onboarding)
        const savedProfile = localStorage.getItem('user_profile');
        const savedToken = localStorage.getItem('token'); // MSAL token for admins
        const currentUser = localStorage.getItem('currentUser'); // Fallback
        
        if (savedProfile) {
          // User completed onboarding - restore their profile
          try {
            const profile = JSON.parse(savedProfile);
            setUser({
              id: profile.email || 'user',
              email: profile.email || '',
              name: profile.name || profile.displayName || '',
              picture: null
            });
          } catch (e) {
            setUser({ id: 'new-user', email: '', name: '', picture: null });
          }
        } else if (savedToken) {
          // Admin user with MSAL token
          setUser({ id: 'msal-user', email: '', name: 'Admin', picture: null });
        } else if (currentUser) {
          // Fallback: user had entered their name before
          setUser({ id: currentUser, email: '', name: currentUser, picture: null });
        } else {
          // New user - app still loads, onboarding will collect email
          setUser({ id: 'new-user', email: '', name: '', picture: null });
        }
      } catch (error) {
        // Session check failed - still allow app to load
        // Still allow app to load - onboarding will handle
        setUser({ id: 'new-user', email: '', name: '', picture: null });
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, []);

  /**
   * Handle Google OAuth callback - DISABLED
   * SECURITY: OAuth callback must exchange code for tokens on backend,
   * not use prompt() or any interactive fallback.
   */
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (code && window.location.pathname === '/auth/google/callback') {
      console.error('❌ SECURITY: Google OAuth callback not implemented. Use email signup or Microsoft Entra ID.');
      // Clear URL parameters - fail closed
      window.history.replaceState({}, document.title, window.location.pathname);
      // Redirect to home
      window.location.href = '/';
    }
  }, []);

  /**
   * Load submitted_on_behalf_of on startup
   */
  useEffect(() => {
    // 1. Try to load saved user profile (from quick signup, etc.)
    const savedProfile = loadUserProfile();
    if (savedProfile && savedProfile.name) {
      setUser(savedProfile.name);
    }
    
    // 2. Try localStorage 'submitted_on_behalf_of'
    const savedSubmittedBy = localStorage.getItem('submitted_on_behalf_of');
    if (savedSubmittedBy) {
      setSubmittedOnBehalfOf(savedSubmittedBy);
    }
    
    // 3. Try localStorage 'currentUser' (legacy)
    const savedCurrentUser = localStorage.getItem('currentUser');
    if (savedCurrentUser) {
      setCurrentUser(savedCurrentUser);
      if (!savedSubmittedBy) {
        setSubmittedOnBehalfOf(savedCurrentUser);
      }
    }
  }, []);

  /**
   * Handle auth callback for /profile route
   */
  useEffect(() => {
    const handleAuthCallback = async () => {
      const hash = window.location.hash;
      const sessionId = hash.split('session_id=')[1];
      
      if (sessionId) {
        try {
          const data = await api.checkSession(sessionId);
          localStorage.setItem('session_token', data.session_token);
          setUser(data.user);
          // Clear the hash from URL
          window.history.replaceState({}, document.title, '/');
        } catch (error) {
          console.error('Authentication failed:', error);
        }
      }
    };

    if (window.location.pathname === '/profile') {
      handleAuthCallback();
    }
  }, []);

  /**
   * Close profile dropdown on outside click
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileDropdown && !event.target.closest('.profile-dropdown-container')) {
        setShowProfileDropdown(false);
      }
    };

    if (showProfileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showProfileDropdown]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================
  
  const value = {
    // State
    user,
    setUser,
    loading,
    setLoading,
    currentUser,
    setCurrentUser,
    submittedOnBehalfOf,
    setSubmittedOnBehalfOf,
    userProfile,
    setUserProfile,
    showSubmittedByModal,
    setShowSubmittedByModal,
    showProfileModal,
    setShowProfileModal,
    showProfileDropdown,
    setShowProfileDropdown,
    authModalMode,
    setAuthModalMode,
    showAttributionModal,
    setShowAttributionModal,
    
    // Functions
    loadUserProfile,
    saveUserProfile,
    getFirstNameFromEmail,
    getSubmittedByName,
    validatePyeBarkerEmail,
    setSubmittedOnBehalfOfUser,
    clearSubmittedOnBehalf,
    logoutUser,
    checkAttributionRequired,
    handleQuickAttribution,
    skipAttribution,
    handleGoogleOAuth,
    handleMicrosoftOAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

/**
 * useAuth hook - Access auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default useAuth;

